/**
 * Iter-52: Reusable quiz-style landing-page form.
 *
 * ONE question per screen with a progress indicator, back button, and
 * client-side navigation (no full-page refresh between steps so the
 * TrustedForm session started by LandingLayout stays intact).
 *
 * Submits to the EXACT SAME `/api/public/landing/lead` endpoint that
 * LandingForm uses — the quiz is a UX change only. The backend applies
 * the same bot defense, TrustedForm capture, custom-page forced routing,
 * TCPA capture, and pixel behavior.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import api from "@/lib/api";
import { routedThankYou } from "@/lib/routedBuyer";
import { formBadgeHidden } from "@/lib/previewBadge";
import { TCPA_LANGUAGE, siteText } from "./config";
import { QUIZ_DEFAULTS, stepsForVertical } from "./quizPresets";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["DC","District of Columbia"],
];

// ---------------------------------------------------------------------------
// Iter-62.3 / Iter-62.3.1: canonical email / ZIP validators reused across
// every public form experience. Kept re-exported here so any existing
// caller (Iter-62.3 tests) can still `import { isValidEmail } from "QuizForm"`.
// ---------------------------------------------------------------------------
export { EMAIL_RE, ZIP_RE, isValidEmail, isValidZip } from "./publicFormValidators";
import { isValidEmail, isValidZip } from "./publicFormValidators";
import { formatUSPhone, isValidUSPhone } from "./publicFormPhone";
import {
  AFPI_CHOICES,
  requiresAtFaultPartyInsured,
} from "./mvaApplicability";
import { emitPreviewEngagement } from "@/lib/prospectEngagement";
import { FIELD_DEFAULTS, fieldRulePage, useFieldRules, zipOk } from "@/lib/useFieldRules";
import { ConsentText, readTrustedFormCert, TrustedFormOffer, trustedFormOn } from "./trustedForm";
import { caseTypeIcon } from "./quizV2/qv2Icons";

const AUTO_TYPES = ["yesno", "select", "case_type", "tristate"];
const V2_ENTRY_TITLE = "What happened? Tap one to start.";

export default function QuizForm(props) {
  return (
    <TrustedFormOffer enabled={trustedFormOn(props)}>
      <QuizFormBody {...props} />
    </TrustedFormOffer>
  );
}

function QuizFormBody({
  vertical,
  preview = false,
  demo = null,
  quizConfig = {},
  progressStyle = "steps",
  session,           // useLandingSession() bundle
  experimentId = null,
  variant = "quiz",
  onFlowChange = null,
}) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const previewSurface = preview || Boolean(demo);
  const cfg = useMemo(
    () => ({ ...QUIZ_DEFAULTS, ...(quizConfig || {}) }),
    [quizConfig],
  );
  const steps = useMemo(
    () => stepsForVertical(vertical.vertical || vertical.slug || "other", cfg),
    [vertical.vertical, vertical.slug, cfg],
  );
  const isV2 = cfg.layout === "v2";
  // -1 = intro screen; Version 2 opens straight on the first question.
  const [i, setI] = useState(() => ((quizConfig || {}).layout === "v2" ? 0 : -1));
  const [answers, setAnswers] = useState({
    case_type: "",
    first_name: "", last_name: "", phone: "", email: "",
    state: "", zip: "",
    // Iter-62.3: `incident_date` remains an OPTIONAL ISO date sent to
    // the backend when the user provides an exact date. Quiz no longer
    // uses a raw calendar picker; the fast-qualifier `incident_age_bucket`
    // below carries the same information in a consumer-friendly window.
    incident_date: "",
    incident_age_bucket: "",
    at_fault: null, currently_represented: null,
    physically_injured: null, medical_treatment: null,
    // Iter-63.1.12A — canonical MVA structured field. Values:
    // "yes" | "no" | "unknown". Required only for MVA case types.
    at_fault_party_insured: null,
    accident_description: "", police_report_number: "",
    accident_photos: [],
    consent: false,
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const advanceTimerRef = useRef(null);
  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY) && !previewSurface;
  const { req } = useFieldRules({ style: "quiz", page: fieldRulePage(vertical), buyer: vertical.buyerCode });

  const affRef = params.get("aff") || "";
  const subId = params.get("sub") || params.get("sub_id") || "";

  useEffect(() => () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  // Track quiz-started once on first-step advance.
  const startedRef = useRef(false);
  // Iter-56.1: mirror `form_start` into the sandboxed tracking bridge
  // exactly once per session. Skipped on preview / demo / sample.
  const formStartFiredRef = useRef(false);
  const emitFormStart = () => {
    if (formStartFiredRef.current) return;
    if (vertical?.isSamplePage) return;
    formStartFiredRef.current = true;
    if (!previewSurface) {
      try {
        window.dispatchEvent(new CustomEvent("lf-tracking-emit", {
          detail: { event: "form_start", context: {} },
        }));
      } catch (_e) { /* noop */ }
    }
    // Iter-63.1: prospect-share `form_start` on demo surfaces only.
    if (demo && vertical?.customSlug && !vertical?.isSamplePage) {
      emitPreviewEngagement(demo, vertical.customSlug, "form_start", {
        isSamplePage: vertical.isSamplePage,
      });
    }
  };
  useEffect(() => {
    if (i >= (isV2 ? 1 : 0) && !startedRef.current) {
      startedRef.current = true;
      session?.trackEvent("quiz_started", { variant, experimentId });
      emitFormStart();
    }
    // Contact step reached beacon.
    const step = i >= 0 ? effectiveSteps[i] : null;
    if (step?.type === "contact") {
      session?.trackEvent("quiz_contact_reached", { variant, experimentId, stepKey: step.key });
    }
    // We intentionally only re-fire on step index change — session /
    // steps / variant are stable across a single quiz walkthrough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  useEffect(() => {
    onFlowChange?.(isV2 && i > 0);
    if (isV2) document.activeElement?.blur?.();
  }, [i, isV2, onFlowChange]);

  // Version 2: each new question gets a browser history entry, so the phone's Back button goes to the previous question.
  const histRef = useRef({ prev: i });
  useEffect(() => {
    if (!isV2) return;
    const h = histRef.current;
    if (i > h.prev) window.history.pushState({ ...(window.history.state || {}), qv2: i }, "");
    h.prev = i;
  }, [i, isV2]);
  useEffect(() => {
    if (!isV2) return undefined;
    // Back and Forward both land on the question saved in that history entry.
    const onPop = (e) => {
      const target = Math.max(0, Number(e.state?.qv2) || 0);
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
      histRef.current.prev = target;
      setI(target);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isV2]);

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  // Global funnels: report answers so a neutral page can name the matched firm.
  useEffect(() => {
    vertical.onAnswers?.(answers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.state, answers.case_type, answers.zip, vertical.onAnswers]);

  // Iter-63.1.12A -- filter step ladder based on effective accident type
  // (AFPI applies only to MVA cases). Rebuild whenever case_type changes.
  const effectiveSteps = useMemo(() => {
    // Same accident type the server uses: the visitor's pick, else the page's own case type.
    const pageType = vertical.vertical && vertical.vertical !== "other" ? vertical.vertical : null;
    const isMva = requiresAtFaultPartyInsured({
      caseType: answers.case_type || pageType,
      verticalSlug: vertical.slug,
    });
    const ruled = (s) => {
      const k = s.key === "evidence" ? "police_report_number" : s.key;
      if (!(k in FIELD_DEFAULTS)) return s;
      const r = req(k);
      const subtitle = r && s.skippable ? null : s.subtitle;
      return { ...s, required: r, skippable: !r, subtitle };
    };
    return steps.filter((s) => !s.onlyIfMvaCaseType || isMva).map(ruled);
  }, [steps, answers.case_type, vertical.slug, vertical.vertical, req]);

  const currentStep = i >= 0 ? effectiveSteps[i] : null;
  const total = effectiveSteps.length;
  const percent = i >= 0 ? Math.round(((i + 1) / total) * 100) : 0;

  const commitStep = (opts = {}) => {
    if (!currentStep) return;
    if (opts.autoAdvance && advanceTimerRef.current) return;   // ignore a double tap mid-advance
    session?.trackEvent("quiz_step_completed", {
      variant, experimentId, stepKey: currentStep.key,
    });
    if (opts.autoAdvance) {
      advanceTimerRef.current = setTimeout(() => {
        advanceTimerRef.current = null;
        setI((n) => Math.min(n + 1, total - 1));
      }, 300);
    } else {
      setI((n) => Math.min(n + 1, total - 1));
    }
  };
  const goBack = () => {
    if (isV2 && i > 0 && Number(window.history.state?.qv2) === i) {
      window.history.back();   // the popstate handler steps back one question
      return;
    }
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    setI((n) => Math.max(n - 1, isV2 ? 0 : -1));
  };

  const validCurrent = () => {
    if (!currentStep) return true;
    const val = currentStep.field ? answers[currentStep.field] : null;
    if (!currentStep.required) return true;
    switch (currentStep.type) {
      case "yesno": return val === true || val === false;
      case "date":  return typeof val === "string" && val.length > 0;
      case "select":
      case "case_type":
      // Iter-62.3: age_bucket is validated identically to select — the
      // consumer must have picked one of the five windows.
      case "age_bucket":
      // Iter-63.1.12A — tri-state MVA question. Consumer picks
      // "yes" | "no" | "unknown"; missing is INCOMPLETE.
      case "tristate": return typeof val === "string" && val.length > 0;
      case "textarea":  return typeof val === "string" && val.trim().length > 0;
      case "evidence":  return answers.police_report_number.trim().length > 0;
      case "consent":   return answers.consent === true;
      case "contact":
        // Iter-74.2 — Email + ZIP are REQUIRED on public landing forms.
        // Both must be present AND parse; blank blocks Continue.
        return Boolean(
          answers.first_name.trim()
          && answers.last_name.trim()
          && isValidUSPhone(answers.phone)
          && answers.state
          && answers.email.trim() && isValidEmail(answers.email)
          && zipOk(answers.zip, req("zip"), isValidZip),
        );
      default: return true;
    }
  };

  const submit = async () => {
    setError("");
    if (previewSurface) {
      // Route directly to Thank-You when demoing a custom page.
      if (demo && vertical.customSlug) {
        const q = new URLSearchParams({
          slug: vertical.customSlug, demo,
          ...(vertical.firmName ? { firm: vertical.firmName } : {}),
          ...(vertical.firmPhone ? { phone: vertical.firmPhone } : {}),
          ...(vertical.funnelSlug ? { funnel: vertical.funnelSlug } : {}),
          ...(vertical.buyerCode ? { b: vertical.buyerCode } : {}),
        });
        nav(`/lp/thank-you?${q.toString()}`);
        return;
      }
      setPreviewSubmitted(true);
      return;
    }
    setSubmitting(true);
    // Iter-54.1: sample landing pages skip the TrustedForm cert read
    // entirely — the backend short-circuits to a DEMO reference anyway,
    // and we never want a demo submission to claim a production cert.
    const isSample = Boolean(vertical.isSamplePage);
    const trustedform_url = isSample ? null : readTrustedFormCert();
    const payload = {
      aff: affRef || null,
      sub_id: subId || null,
      landing_page: vertical.slug,
      case_type: answers.case_type || null,
      first_name: answers.first_name.trim(),
      last_name: answers.last_name.trim(),
      phone: answers.phone.trim(),
      email: answers.email.trim() || null,
      state: answers.state,
      zip: answers.zip.trim() || null,
      // Iter-62.3: `incident_date` stays OPTIONAL — the Quiz now
      // primarily carries the fast-qualifier bucket. The backend
      // `_check_incident_date` gate soft-accepts null (line 210 in
      // pi_validation.py), so leaving it null does not trip the SOL
      // filter. If a downstream buyer later requires the exact date
      // we can add a follow-up step per Iter-62.3 spec.
      incident_date: answers.incident_date || null,
      incident_age_bucket: answers.incident_age_bucket || null,
      at_fault: answers.at_fault,
      currently_represented: answers.currently_represented,
      physically_injured: answers.physically_injured,
      medical_treatment: answers.medical_treatment,
      // Iter-63.1.12A — canonical MVA structured field.
      at_fault_party_insured: answers.at_fault_party_insured || null,
      accident_description: answers.accident_description.trim() || null,
      police_report_number: answers.police_report_number.trim() || null,
      accident_photo_ids: (answers.accident_photos || [])
        .filter((p) => p.file_id && !p.uploading).map((p) => p.file_id),
      consent: answers.consent,
      tcpa_language: (vertical.tcpaLanguage && vertical.tcpaLanguage.trim())
        ? vertical.tcpaLanguage : TCPA_LANGUAGE,
      trustedform_url,
      landing_url: window.location.href,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
      turnstile_token: turnstileToken || null,
      company: null,   // Quiz honeypot lives inline below
      // Iter-52: `load_ms` reflects the ENTIRE quiz session, not just
      // the final contact-step mount — bot defense stays honest.
      load_ms: session ? session.elapsedMs() : null,
      // Iter-52: quiz + A/B attribution.
      form_experience: "quiz",
      experiment_id: experimentId,
      experiment_variant: variant,
      quiz_session_id: session?.sessionId || null,
      buyer_code: vertical.buyerCode || null,
      buyer_source: vertical.buyerSource || null,
    };
    try {
      session?.trackEvent("submit_click", { variant, experimentId });
      const { data } = await api.post("/public/landing/lead", payload);
      const q = new URLSearchParams({
        lead_id: data.id,
        vertical: vertical.slug,
        ...(data.is_test ? { test: "1" } : {}),
        ...routedThankYou(vertical, data),
        ...(vertical.funnelSlug ? { funnel: vertical.funnelSlug } : {}),
      });
      nav(`/lp/thank-you?${q.toString()}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || "Something went wrong — please try again.";
      setError(typeof msg === "string" ? msg
                : "Submission failed. Please try again.");
      // A Turnstile token works once: get a fresh one for the retry.
      setTurnstileToken("");
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  if (previewSubmitted) {
    return (
      <div className="lp-form-card" data-testid="quiz-preview-submitted">
        <div className="lp-preview-success">
          <strong>PREVIEW SUBMITTED</strong>
          <p>This is a preview — no real lead was sent.</p>
        </div>
      </div>
    );
  }

  // Intro screen
  if (i < 0) {
    return (
      <div className="lp-form-card" data-testid="quiz-form">
        {previewSurface && !formBadgeHidden() && (
          <div className="lp-quiz-preview-banner" data-testid="quiz-preview-banner">
            PREVIEW · No submission will be sent
          </div>
        )}
        <div className="lp-quiz-intro" data-testid="quiz-intro">
          <div className="lp-quiz-eyebrow">Free case review</div>
          <h2 className="lp-quiz-title" data-testid="quiz-intro-headline">
            {cfg.intro_headline}
          </h2>
          <p className="lp-quiz-sub" data-testid="quiz-intro-sub">
            {cfg.intro_subheadline}
          </p>
          <button
            type="button"
            className="lp-cta lp-quiz-start"
            data-testid="quiz-start-btn"
            onClick={() => setI(0)}
          >
            Start My Free Review →
          </button>
          <div className="lp-quiz-meta">
            {total} quick questions · takes about 60 seconds
          </div>
        </div>
      </div>
    );
  }

  const entry = isV2 && i === 0;
  const autoStep = AUTO_TYPES.includes(currentStep.type) || (isV2 && currentStep.type === "age_bucket");
  const tiles = isV2 && currentStep.key === "case_type";
  const stepKey = isV2 ? currentStep.key : undefined;

  return (
    <div
      className={`lp-form-card${isV2 ? " qv2-card" : ""}${entry ? " qv2-card-entry" : ""}`}
      data-qv2={isV2 ? (entry ? "entry" : "flow") : undefined}
      data-testid={`quiz-step-${currentStep.key}`}
    >
      {previewSurface && !formBadgeHidden() && (
        <div className="lp-quiz-preview-banner" data-testid="quiz-preview-banner">
          PREVIEW · No submission will be sent
        </div>
      )}

      {/* Progress indicator (Version 2 hides it on the first-page question) */}
      {entry ? (
        <div className="lp-quiz-eyebrow" data-testid="quiz-v2-entry-eyebrow">Free case review · about 60 seconds</div>
      ) : (
        <div className="lp-quiz-progress" data-testid="quiz-progress">
          {(progressStyle === "percent" || isV2) && (
            <div className="lp-quiz-progress-bar">
              <div className="lp-quiz-progress-fill" style={{ width: `${percent}%` }} />
            </div>
          )}
          <div className="lp-quiz-progress-label" data-testid="quiz-progress-label">
            {progressStyle === "percent" ? `${percent}% complete` : `Question ${i + 1} of ${total}`}
          </div>
        </div>
      )}

      <h2 className="lp-quiz-title" key={stepKey && `title-${stepKey}`} data-testid="quiz-step-title">
        {entry && tiles ? V2_ENTRY_TITLE : currentStep.title}
      </h2>
      {currentStep.subtitle && !(entry && tiles) && (
        <p className="lp-quiz-sub">{currentStep.subtitle}</p>
      )}

      {/* Step body */}
      <div className="lp-quiz-body" key={stepKey && `body-${stepKey}`}>
        {currentStep.type === "yesno" && (
          <div className="lp-quiz-yesno">
            <button
              type="button"
              className={"lp-quiz-choice " + (answers[currentStep.field] === true ? "active" : "")}
              onClick={() => { setAnswer(currentStep.field, true); commitStep({ autoAdvance: true }); }}
              data-testid={`quiz-choice-yes-${currentStep.key}`}
            >Yes</button>
            <button
              type="button"
              className={"lp-quiz-choice " + (answers[currentStep.field] === false ? "active" : "")}
              onClick={() => { setAnswer(currentStep.field, false); commitStep({ autoAdvance: true }); }}
              data-testid={`quiz-choice-no-${currentStep.key}`}
            >No</button>
          </div>
        )}

        {currentStep.type === "date" && (
          <input
            type="date"
            className="lp-input lp-quiz-input"
            value={answers[currentStep.field] || ""}
            onChange={(e) => setAnswer(currentStep.field, e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            data-testid={`quiz-input-${currentStep.key}`}
          />
        )}

        {/* Iter-63.1.2: age-bucket step is a dropdown with plain-English
            labels. Auto-advance preserved on non-empty selection. */}
        {currentStep.type === "age_bucket" && isV2 && (
          <div className="lp-quiz-options" data-testid={`quiz-age-bucket-${currentStep.key}`}>
            {(currentStep.options || []).map((o) => (
              <button
                key={o.value}
                type="button"
                className={"lp-quiz-option " + (answers[currentStep.field] === o.value ? "active" : "")}
                onClick={() => { setAnswer(currentStep.field, o.value); commitStep({ autoAdvance: true }); }}
                data-testid={`quiz-age-bucket-${o.value}`}
              >{o.label}</button>
            ))}
          </div>
        )}
        {currentStep.type === "age_bucket" && !isV2 && (
          <select
            className="lp-select lp-quiz-input"
            value={answers[currentStep.field] || ""}
            onChange={(e) => {
              const v = e.target.value;
              setAnswer(currentStep.field, v);
              if (v) commitStep({ autoAdvance: true });
            }}
            data-testid={`quiz-age-bucket-${currentStep.key}`}
          >
            <option value="">Select when the accident happened…</option>
            {(currentStep.options || []).map((o) => (
              <option
                key={o.value}
                value={o.value}
                data-testid={`quiz-age-bucket-${o.value}`}
              >{o.label}</option>
            ))}
          </select>
        )}

        {(currentStep.type === "select" || currentStep.type === "case_type") && (
          <div className={tiles ? "qv2-tiles" : "lp-quiz-options"}>
            {(currentStep.options || []).map((o) => {
              const Icon = tiles ? caseTypeIcon(o.value) : null;
              return (
                <button
                  key={o.value}
                  type="button"
                  className={(tiles ? "qv2-tile " : "lp-quiz-option ") + (answers[currentStep.field] === o.value ? "active" : "")}
                  onClick={() => { setAnswer(currentStep.field, o.value); commitStep({ autoAdvance: true }); }}
                  data-testid={`quiz-option-${currentStep.key}-${o.value}`}
                >
                  {Icon ? <><Icon size={28} weight="duotone" aria-hidden="true" /><span>{o.label}</span></> : o.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Iter-63.1.12A — canonical tri-state MVA question (AFPI). Three
            large touch targets with auto-advance on choice; matches the
            visual pattern of the yesno + select steps. */}
        {currentStep.type === "tristate" && (
          <div className="lp-quiz-options" data-testid={`quiz-tristate-${currentStep.key}`}>
            {(currentStep.options || []).map((o) => (
              <button
                key={o.value}
                type="button"
                className={"lp-quiz-option " + (answers[currentStep.field] === o.value ? "active" : "")}
                onClick={() => { setAnswer(currentStep.field, o.value); commitStep({ autoAdvance: true }); }}
                data-testid={`quiz-tristate-${currentStep.key}-${o.value}`}
              >{o.label}</button>
            ))}
          </div>
        )}

        {currentStep.type === "textarea" && (
          <textarea
            className="lp-textarea lp-quiz-input"
            rows={4}
            placeholder="Add anything the attorney should know…"
            value={answers[currentStep.field] || ""}
            onChange={(e) => setAnswer(currentStep.field, e.target.value)}
            data-testid={`quiz-input-${currentStep.key}`}
          />
        )}

        {currentStep.type === "evidence" && (
          <div className="lp-quiz-evidence" data-testid="quiz-evidence">
            <label className="lp-label">
              Police report number{req("police_report_number") ? <span className="lp-req">*</span> : " (optional)"}
            </label>
            <input
              type="text"
              className="lp-input"
              value={answers.police_report_number}
              onChange={(e) => setAnswer("police_report_number", e.target.value)}
              placeholder="e.g. 2025-1234"
              data-testid="quiz-evidence-report"
            />
            <div className="lp-note">
              You&apos;ll be able to upload accident photos with your attorney later if needed.
            </div>
          </div>
        )}

        {currentStep.type === "contact" && (
          <form className="lp-quiz-contact" autoComplete="on" onSubmit={(e) => e.preventDefault()} data-testid="quiz-contact">
            <div className="lp-fields-row">
              <div className="lp-field">
                <label className="lp-label" htmlFor="quiz-first">First name</label>
                <input
                  id="quiz-first" name="first_name" autoComplete="given-name"
                  type="text" className="lp-input"
                  value={answers.first_name}
                  onChange={(e) => setAnswer("first_name", e.target.value)}
                  data-testid="quiz-first-name"
                />
              </div>
              <div className="lp-field">
                <label className="lp-label" htmlFor="quiz-last">Last name</label>
                <input
                  id="quiz-last" name="last_name" autoComplete="family-name"
                  type="text" className="lp-input"
                  value={answers.last_name}
                  onChange={(e) => setAnswer("last_name", e.target.value)}
                  data-testid="quiz-last-name"
                />
              </div>
            </div>
            <div className="lp-fields-row">
              <div className="lp-field">
                <label className="lp-label" htmlFor="quiz-phone">Phone</label>
                <input
                  id="quiz-phone" name="phone"
                  type="tel" className="lp-input"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="585-545-8888"
                  value={answers.phone}
                  onChange={(e) => setAnswer("phone", formatUSPhone(e.target.value))}
                  data-testid="quiz-phone"
                />
              </div>
              <div className="lp-field">
                {/* Iter-74.2 — Email REQUIRED. */}
                <label className="lp-label" htmlFor="quiz-email">Email<span className="lp-req">*</span></label>
                <input
                  id="quiz-email" name="email" autoComplete="email"
                  type="email" className="lp-input" required
                  value={answers.email}
                  onChange={(e) => setAnswer("email", e.target.value)}
                  data-testid="quiz-email"
                />
              </div>
            </div>
            <div className="lp-fields-row">
              <div className="lp-field">
                <label className="lp-label" htmlFor="quiz-state">State</label>
                <select
                  id="quiz-state" name="state" autoComplete="address-level1"
                  className="lp-input"
                  value={answers.state}
                  onChange={(e) => setAnswer("state", e.target.value)}
                  data-testid="quiz-state"
                >
                  <option value="">Choose…</option>
                  {US_STATES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                </select>
              </div>
              <div className="lp-field">
                {/* Iter-74.2 — ZIP REQUIRED. */}
                <label className="lp-label" htmlFor="quiz-zip">ZIP{req("zip") ? <span className="lp-req">*</span> : " (optional)"}</label>
                <input
                  id="quiz-zip" name="zip" autoComplete="postal-code" inputMode="numeric"
                  type="text" className="lp-input" required={req("zip")}
                  value={answers.zip}
                  onChange={(e) => setAnswer("zip", e.target.value)}
                  data-testid="quiz-zip"
                />
              </div>
            </div>
          </form>
        )}

        {currentStep.type === "consent" && (
          <div className="lp-quiz-consent" data-testid="quiz-consent">
            <label className="lp-consent-label" data-tf-element-role="consent-language">
              <input
                type="checkbox"
                name="consent"
                checked={answers.consent}
                onChange={(e) => setAnswer("consent", e.target.checked)}
                data-tf-element-role="consent-opt-in"
                data-testid="quiz-consent-check"
                className="lp-consent-check"
              />
              <span
                className="lp-consent-text tf-tagged-consent"
                data-testid="quiz-consent-text"
              >
                <ConsentText
                  text={siteText((vertical.tcpaLanguage && vertical.tcpaLanguage.trim()) ? vertical.tcpaLanguage : TCPA_LANGUAGE)}
                  advertiser={vertical.firmName}
                />
              </span>
            </label>
            {turnstileEnabled && (
              <div className="lp-turnstile-wrap" data-testid="quiz-turnstile">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(t) => setTurnstileToken(t)}
                  onError={() => setTurnstileToken("")}
                  onExpire={() => setTurnstileToken("")}
                  options={{ theme: "light", size: "flexible" }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="lp-error" data-testid="quiz-error">{error}</div>
      )}

      {/* Navigation (none on the Version 2 first-page question — the tap moves on) */}
      {!(entry && autoStep) && (
      <div className="lp-quiz-nav">
        {i > (isV2 ? 0 : -1) && (
          <button
            type="button"
            className="lp-btn-ghost"
            onClick={goBack}
            data-testid="quiz-back-btn"
          >← Back</button>
        )}
        <div style={{ flex: 1 }} />
        {currentStep.skippable && (
          <button
            type="button"
            className="lp-btn-ghost"
            onClick={() => commitStep()}
            data-testid={`quiz-skip-${currentStep.key}`}
          >Skip</button>
        )}
        {currentStep.type === "consent" ? (
          <button
            type="button"
            className="lp-cta"
            disabled={submitting || !validCurrent()
                      || (turnstileEnabled && !turnstileToken)}
            onClick={submit}
            name="submit"
            data-tf-element-role="submit"
            data-testid="quiz-submit-btn"
          >
            {submitting ? "Submitting…" : "Get My Free Case Review"}
          </button>
        ) : !autoStep && (
          <button
            type="button"
            className="lp-cta"
            disabled={!validCurrent()}
            onClick={() => commitStep()}
            data-testid={`quiz-continue-${currentStep.key}`}
          >Continue →</button>
        )}
      </div>
      )}
    </div>
  );
}
